import { useMemo } from 'react';

interface CadScaleBarProps {
  frustumWidth: number;
  canvasWidth: number;
  metersPerUnit: number;
}

const NICE_DISTANCES = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

function formatDistance(meters: number): string {
  if (meters < 0.01) return `${(meters * 1000).toFixed(0)} mm`;
  if (meters < 1) return `${(meters * 100).toFixed(0)} cm`;
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${meters} m`;
}

export function CadScaleBar({ frustumWidth, canvasWidth, metersPerUnit }: CadScaleBarProps) {
  const { barWidth, label } = useMemo(() => {
    if (canvasWidth <= 0 || frustumWidth <= 0) return { barWidth: 0, label: '' };

    const worldUnitsPerPixel = frustumWidth / canvasWidth;
    const metersPerPixel = worldUnitsPerPixel * metersPerUnit;

    let bestDist = NICE_DISTANCES[0];
    for (const d of NICE_DISTANCES) {
      const px = d / metersPerPixel;
      if (px >= 60 && px <= 200) {
        bestDist = d;
        break;
      }
      if (px < 60) bestDist = d;
    }

    const px = bestDist / metersPerPixel;
    return { barWidth: Math.round(px), label: formatDistance(bestDist) };
  }, [frustumWidth, canvasWidth, metersPerUnit]);

  if (barWidth <= 0) return null;

  return (
    <div className="absolute bottom-[22px] left-6 z-50 pointer-events-none flex flex-col items-start gap-1">
      <span className="text-[10px] text-gray-400 font-mono">{label}</span>
      <div className="h-[2px] bg-[#d4ff33] rounded-full relative" style={{ width: barWidth }}>
        <div className="absolute -left-[1px] -top-[3px] w-[2px] h-[8px] bg-[#d4ff33] rounded-full" />
        <div className="absolute -right-[1px] -top-[3px] w-[2px] h-[8px] bg-[#d4ff33] rounded-full" />
      </div>
    </div>
  );
}
